import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TableRow, Table, TableHead, TableBody, TableContainer, Paper, TableCell, Checkbox, FormControlLabel } from "@material-ui/core";
import ScheduleService from "./Locality/Service/scheduleService";
import SchoolService from "./Locality/Service/schoolService";
import * as Yup from 'yup';
import { Grid } from "@material-ui/core";
import { useFormik } from 'formik';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Widget from "../../components/Widget/Widget";
import PageTitle from "../../components/PageTitle/PageTitle";
import { Typography } from "../../components/Wrappers/Wrappers";
const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: "#0f3b62ad",
        color: theme.palette.common.white,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },
    tableOverflow: {
        overflowX: 'auto',
    },
}));

export default function Schedule() {
    const tableHeaders = ['Frequency', 'School', 'Notify before publish', 'Edit', 'Delete'];
    const classes = useStyles();
    const [scheduleList, setScheduleList] = useState([]);
    const [schoolList, setSchoolList] = useState([]);
    const [registeredSchoolsCount, setRegisteredSchoolsCount] = useState(0);
    const [registeredSchoolNames, setRegisteredSchoolNames] = useState([]);
    const [selectedRegisteredSchool, setSelectedRegisteredSchool] = useState("");
    const [open, setOpen] = useState(false);
    const [schedule, setSchedule] = useState({
        frequency: '',
        schoolId: '',
        isNotifyBeforePublish: false,
    });

    const userDetails = localStorage.getItem("userDetail")
        ? JSON.parse(localStorage.getItem("userDetail"))
        : {};
    const currentSchoolId = userDetails?.schooleId || userDetails?.schoolId || userDetails?._id;
    const isSchoolRole = userDetails?.role === "SCHOOLE";
    
    useEffect(() => {
        getScheduleList();
        getSchoolList();
        // SCHOOLE users should only see their own schedules.
        // Avoid calling the global count API since we filter schedules client-side.
        if (!isSchoolRole) {
            getRegisteredSchoolsCount();
        }
        return () => {
            setScheduleList([]);
        }
    }, []);

    const getSchoolList = () => {
        // SCHOOLE should only see its own school in the popup
        if (isSchoolRole && currentSchoolId) {
            SchoolService.getAllSchoolList()
                .then((res) => {
                    const all = Array.isArray(res) ? res : [];
                    const onlyCurrent = all.filter((s) => String(s?._id) === String(currentSchoolId));
                    setSchoolList(onlyCurrent);
                })
                .catch((err) => {
                    console.error("Error fetching schools:", err);
                    setSchoolList([]);
                });
            return;
        }

        SchoolService.getAllSchoolList()
            .then((res) => {
                setSchoolList(res || []);
            })
            .catch((err) => {
                console.error("Error fetching schools:", err);
            });
    };

    const handleOpen = (status) => {
        if(status){
            if (isSchoolRole && currentSchoolId) {
                setSchedule({
                    frequency: "",
                    schoolId: currentSchoolId,
                    isNotifyBeforePublish: false,
                });
            } else {
            setSchedule({
                frequency: '',
                schoolId: '',
                isNotifyBeforePublish: false,
            });
            }
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    
    const getScheduleList = () => {
        ScheduleService.getAllSchedule().then((res) => {
            const schedules = Array.isArray(res) ? res : [];
            const filteredSchedules =
                isSchoolRole && currentSchoolId
                    ? schedules.filter((s) => {
                        const id = s?.schoolId?._id || s?.schoolId;
                        return String(id) === String(currentSchoolId);
                      })
                    : schedules;

            setScheduleList(filteredSchedules);
            // Extract unique registered school names
            getRegisteredSchoolNames(filteredSchedules);
        }).catch((err) => {
            console.error('Error fetching schedules:', err);
        });
    };

    // Get unique school names from schedule list
    const getRegisteredSchoolNames = (schedules) => {
        const uniqueSchools = new Map();
        schedules.forEach(schedule => {
            const schoolId = schedule.schoolId?._id || schedule.schoolId;
            const schoolName = schedule.schoolId?.schooleName || schedule.schoolId?.schoolName;
            
            if (schoolId && schoolName) {
                const id = schoolId.toString();
                if (!uniqueSchools.has(id)) {
                    uniqueSchools.set(id, schoolName);
                }
            }
        });
        
        const schoolNamesArray = Array.from(uniqueSchools.values());
        setRegisteredSchoolNames(schoolNamesArray);
        setRegisteredSchoolsCount(schoolNamesArray.length);
    };

    const getRegisteredSchoolsCount = () => {
        ScheduleService.getRegisteredSchoolsCount().then((res) => {
            setRegisteredSchoolsCount(res.count || 0);
        }).catch((err) => {
            console.error('Error fetching registered schools count:', err);
            // Fallback to client-side calculation if API fails
            const uniqueSchoolIds = new Set();
            scheduleList.forEach(schedule => {
                const schoolId = schedule.schoolId?._id || schedule.schoolId;
                if (schoolId) {
                    uniqueSchoolIds.add(schoolId.toString());
                }
            });
            setRegisteredSchoolsCount(uniqueSchoolIds.size || 0);
        });
    };

    const validationSchema = Yup.object().shape({
        frequency: Yup.string().required('Frequency is required'),
        schoolId: Yup.string().required('School is required'),
    });
   
    const editSchedule = (scheduleItem) => {
        setSchedule({
            ...scheduleItem,
            schoolId: scheduleItem.schoolId?._id || scheduleItem.schoolId || '',
            isNotifyBeforePublish: Boolean(scheduleItem.isNotifyBeforePublish),
        });
        setOpen(true);
    };

    const deleteSchedule = (scheduleDelete) => {
        if (scheduleDelete && window.confirm('Are you sure you want to delete this schedule?')) {
            ScheduleService.deleteSchedule(scheduleDelete).then((res) => {
                getScheduleList();
                getRegisteredSchoolsCount();
                alert('Schedule deleted successfully.');
            }).catch((err) => {
                alert('Error deleting schedule: ' + (err.response?.data?.message || err.message));
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatFrequency = (cronExpression) => {
        if (!cronExpression) return 'N/A';

        const frequencyMap = {
            // '* * * * *': 'Every 5 Minute',
            '0 0 * * 5': 'Weekly on Friday',
            EVERY_2_WEEKS_FRIDAY: 'Every 2 Weeks on Friday',
            '0 0 1 * *': 'Monthly on 1st',
            '0 0 15 * *': 'Monthly on 15th',
        };

        return frequencyMap[cronExpression] || cronExpression;
    };


    const formik = useFormik({
        initialValues: schedule,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            const submitData = {
                ...values,
                isNotifyBeforePublish: Boolean(values.isNotifyBeforePublish),
            };

            if(schedule._id){
                ScheduleService.updateSchedule({ ...submitData, _id: schedule._id }).then((res) => {
                    handleClose();
                    getScheduleList();
                    getRegisteredSchoolsCount();
                    resetForm();
                    alert("Schedule Updated Successfully.");
                }).catch((err) => {
                    alert(err.response?.data?.message || 'Error updating schedule');
                });
            } else {
                ScheduleService.createSchedule(submitData).then((res) => {
                    getScheduleList();
                    getRegisteredSchoolsCount();
                    handleClose();
                    resetForm();
                    alert("Schedule Added Successfully.");
                }).catch((err) => {
                    alert(err.response?.data?.message || 'Error creating schedule');
                });
            }
        },
    });

    return (
        <>
            <PageTitle title="Schedule" button={<Button
                variant="contained" 
                onClick={() => handleOpen(true)}
                size="medium"
                color="secondary" 
                style={{backgroundColor:'rgb(15 59 98)'}}> 
                Add Schedule
            </Button>} />
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Widget title="About Schedule" upperTitle>
                        <div style={{ padding: 16 }}>
                            <Typography variant="body1" style={{ fontWeight: 700, color: "#0f3b62" }}>
                                What is Schedule?
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                                The schedule automatically publishes planned activities to the Parent module according to the selected frequency (weekly, bi-weekly, monthly, etc.).
                            </Typography>

                            <Typography variant="body1" style={{ fontWeight: 700, color: "#0f3b62", marginTop: 14 }}>
                                How to use it
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                                Choose the frequency and school, then save. When the schedule runs, it will process all pending draft activities for the selected school.
                            </Typography>

                            <Typography variant="body1" style={{ fontWeight: 700, color: "#0f3b62", marginTop: 14 }}>
                                Notify before publishing (approval required)
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6, lineHeight: 1.6 }}>
                                If <b>No</b>: Activities are automatically published to parents when the schedule runs.
                                <br />
                                <br />
                                If <b>Yes</b>: Activities remain in draft. The School Admin receives a header notification to approve them, and they are published to parents only after approval.
                            </Typography>
                        </div>
                    </Widget>
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                    <Widget
                        title={`Registered Schools (${registeredSchoolsCount})`}
                        upperTitle
                    >
                        <div style={{ padding: '20px' }}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel shrink id="registered-schools-label">
                                    Select School
                                </InputLabel>
                                <Select
                                    labelId="registered-schools-label"
                                    id="registered-schools-select"
                                    label="Select School"
                                    value={selectedRegisteredSchool}
                                    displayEmpty
                                    onChange={(e) => setSelectedRegisteredSchool(e.target.value)}
                                    renderValue={(val) => (val ? val : <em>Select a registered school</em>)}
                                >
                                    <MenuItem value="">
                                        <em>Select a registered school</em>
                                    </MenuItem>
                                    {registeredSchoolNames.length > 0 ? (
                                        registeredSchoolNames.map((schoolName, index) => (
                                            <MenuItem key={index} value={schoolName}>
                                                {schoolName}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="" disabled>
                                            <em>No schools registered yet</em>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            {registeredSchoolNames.length > 0 && (
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: '16px', textAlign: 'center' }}>
                                    {registeredSchoolsCount} {registeredSchoolsCount === 1 ? 'school' : 'schools'} registered
                                </Typography>
                            )}
                        </div>
                    </Widget>
                </Grid>
                <Grid item xs={12}>
                    <Widget title="" upperTitle noBodyPadding bodyClass={classes.tableOverflow}>
                        <TableContainer component={Paper} style={{ maxHeight: 500 }}>
                            <Table stickyHeader className="mb-0">
                                <TableHead>
                                    <TableRow>
                                        {tableHeaders.map(key => (
                                            <StyledTableCell key={key}>{key}</StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scheduleList.map((scheduleItem) => (
                                        <TableRow key={scheduleItem._id}>
                                            <TableCell className="pl-3 fw-normal">
                                                <div>{formatFrequency(scheduleItem.frequency)}</div>
                                                {!scheduleItem.frequency.startsWith('EVERY_') && (
                                                    <div style={{ fontSize: '0.75rem', color: '#666' }}>{scheduleItem.frequency}</div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {scheduleItem.schoolId?.schooleName || scheduleItem.schoolId || 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                {Boolean(scheduleItem.isNotifyBeforePublish) ? 'Yes' : 'No'}
                                            </TableCell>
                                            <TableCell>
                                                <EditIcon 
                                                    style={{ cursor: 'pointer' }} 
                                                    onClick={() => editSchedule(scheduleItem)} 
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <DeleteIcon 
                                                    style={{ cursor: 'pointer' }} 
                                                    onClick={() => deleteSchedule(scheduleItem)} 
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Widget>
                </Grid>
            </Grid>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                PaperProps={{ style: { borderRadius: 14, maxWidth: 420 } }}
            >
                <DialogTitle style={{ fontWeight: 800, color: "#0f3b62" }}>
                    {schedule._id ? "Edit Schedule" : "Add Schedule"}
                </DialogTitle>
                <form onSubmit={formik.handleSubmit}>
                    <DialogContent style={{ paddingTop: 12, paddingBottom: 0 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth margin="dense">
                                    <InputLabel id="frequency-label">Frequency (Cron Schedule)</InputLabel>
                                    <Select
                                        labelId="frequency-label"
                                        id="frequency"
                                        name="frequency"
                                        value={formik.values.frequency}
                                        onChange={formik.handleChange}
                                        error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                                        label="Frequency (Cron Schedule)"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {/* <MenuItem value="* * * * *">Every 5 Minute</MenuItem> */}
                                        <MenuItem value="0 0 * * 5">Weekly on Friday</MenuItem>
                                        <MenuItem value="EVERY_2_WEEKS_FRIDAY">Every 2 Weeks on Friday</MenuItem>
                                        <MenuItem value="0 0 1 * *">Monthly on 1st</MenuItem>
                                        <MenuItem value="0 0 15 * *">Monthly on 15th</MenuItem>
                                    </Select>
                                    {formik.touched.frequency && formik.errors.frequency && (
                                        <div style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>
                                            {formik.errors.frequency}
                                        </div>
                                    )}
                                    {formik.values.frequency && (
                                        <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 4 }}>
                                            {formik.values.frequency.startsWith("EVERY_")
                                                ? `Schedule: ${formatFrequency(formik.values.frequency)}`
                                                : `Cron: ${formik.values.frequency}`}
                                        </div>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth margin="dense">
                                    <InputLabel id="school-label">School</InputLabel>
                                    <Select
                                        labelId="school-label"
                                        id="schoolId"
                                        name="schoolId"
                                        value={formik.values.schoolId}
                                        onChange={formik.handleChange}
                                        error={formik.touched.schoolId && Boolean(formik.errors.schoolId)}
                                        disabled={isSchoolRole && Boolean(currentSchoolId)}
                                        label="School"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {schoolList.map((school) => (
                                            <MenuItem key={school._id} value={school._id}>
                                                {school.schooleName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.schoolId && formik.errors.schoolId && (
                                        <div style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>
                                            {formik.errors.schoolId}
                                        </div>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={Boolean(formik.values.isNotifyBeforePublish)}
                                            onChange={(e) => formik.setFieldValue('isNotifyBeforePublish', e.target.checked)}
                                        />
                                    }
                                    label="Notify before publishing (approval required)"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 10, padding: "6px 0" }}>
                            <Button onClick={handleClose} variant="outlined">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                Save
                            </Button>
                        </div>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}

